/* eslint-disable  consistent-return */
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
const ChallengeSelect = ({ challenges = [], value, onChange, loading, disabled }) => {
  const inputId = useId();
  const listboxId = useId();
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const isDisabled = loading || disabled;

  // 1. Memoize selected item lookup
  const selected = useMemo(() => {
    if (!value || !challenges.length) return null;
    const stringVal = String(value);
    return challenges.find((c) => String(c.id) === stringVal) || null;
  }, [challenges, value]);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);

  // 2. Combined Sync & Outside Click Logic into a single effect listener
  // Combined Sync & Outside Click Logic with consistent returns for ESLint
useEffect(() => {
  setQuery(selected?.title || "");

  // If not open, do not add the event listener, and return early with no cleanup function
  if (!open) {
    return; 
  }

  const handleOutsideClick = (e) => {
    if (!wrapRef.current?.contains(e.target)) {
      setOpen(false);
      setActiveIndex(-1);
      setQuery(selected?.title || "");
    }
  };

  document.addEventListener("mousedown", handleOutsideClick);
  
  return () => {
    document.removeEventListener("mousedown", handleOutsideClick);
  };
}, [selected, open]);


  // 3. Optimized filtering loop
  // Optimized filtering loop with consistent returns
const filtered = useMemo(() => {
  const q = query.trim().toLowerCase();
  
  if (!q) {
    return challenges;
  }
  
  // Quick exit condition if the query matches the current selection exactly
  if (selected && q === selected.title.trim().toLowerCase()) {
    return challenges;
  }

  return challenges.filter((c) => c.title?.toLowerCase().includes(q));
}, [challenges, query, selected]);


  const selectItem = (item) => {
    onChange(String(item.id));
    setQuery(item.title || "");
    setOpen(false);
    setActiveIndex(-1);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (isDisabled) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setOpen(true);
        setActiveIndex((prev) => (prev + 1 >= filtered.length ? 0 : prev + 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setOpen(true);
        setActiveIndex((prev) => (prev - 1 < 0 ? filtered.length - 1 : prev - 1));
        break;
      case "Enter":
        if (!open) return;
        e.preventDefault();
        if (filtered[activeIndex]) selectItem(filtered[activeIndex]);
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
        setQuery(selected?.title || "");
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapRef} style={{ marginBottom: 8, position: "relative" }}>
      <label htmlFor={inputId} style={{ fontSize: '0.825rem' }}>
        {/* Select a challenge */}
        <div style={{ marginTop: '1rem' }}>
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            value={query}
            disabled={isDisabled}
            placeholder={loading ? "Loading..." : "Search a challenge..."}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              setActiveIndex(-1);
            }}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              open && activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined
            }
            style={{
              width: "100%",
              padding: "1rem 1.125rem",
              border: "2px solid #e1e5e9",
              borderRadius: 8,
              outline: "none",
              boxSizing: 'border-box'
            }}
          />
        </div>
      </label>

      {open && !isDisabled && (
        <ul
          id={listboxId}
          role="listbox"
          style={{
            fontFamily: 'VodafoneRg-Regular',
            position: "absolute",
            zIndex: 20,
            width: "100%",
            marginTop: 6,
            maxHeight: 240,
            overflow: "auto",
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 8,
            padding: 0,
            listStyle: "none",
            boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
          }}
        >
          {filtered.length === 0 ? (
            <li style={{ padding: 10, color: "#777" }}>No results found.</li>
          ) : (
            filtered.map((c, idx) => {
              const isSelected = String(c.id) === String(value);
              return (
                <li
                  key={c.id}
                  id={`${listboxId}-opt-${idx}`}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectItem(c)}
                  style={{
                    padding: "10px 12px",
                    cursor: "pointer",
                    background: idx === activeIndex ? "#f2f4ff" : "#fff",
                    borderBottom: "1px solid #f2f2f2",
                    fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  {c.title}
                </li>
              );
            })
          )}
        </ul>
      )}

      {!loading && challenges.length === 0 && (
        <div style={{ marginTop: 8, color: "#777" }}>No challenges found.</div>
      )}
    </div>
  );
};

export default ChallengeSelect;
